import type { JSONContent } from '@tiptap/core'

export type DiffTone = 'added' | 'deleted'
type DiffPresentation = 'side-by-side' | 'inline'

const ADDED_MARK = {
  type: 'highlight',
  attrs: { color: 'var(--status-green-bg)' },
}

const DELETED_MARK = {
  type: 'highlight',
  attrs: { color: 'var(--status-red-bg)' },
}

const STRIKE_MARK = {
  type: 'strike',
}

export function createAnnotatedDocuments(
  previous: JSONContent,
  current: JSONContent
) {
  const annotated = annotateNodePair(previous, current)

  return {
    previous: annotated.previous[0] ?? previous,
    current: annotated.current[0] ?? current,
  }
}

export function createInlineAnnotatedDocument(
  previous: JSONContent,
  current: JSONContent
) {
  const annotated = annotateInlineNodePair(previous, current)
  return annotated[0] ?? current
}

function annotateNodePair(
  previous?: JSONContent,
  current?: JSONContent
): { previous: JSONContent[]; current: JSONContent[] } {
  if (!previous && !current) {
    return { previous: [], current: [] }
  }

  if (!previous && current) {
    return {
      previous: [],
      current: [markTextInNode(current, 'added', 'side-by-side')],
    }
  }

  if (previous && !current) {
    return {
      previous: [markTextInNode(previous, 'deleted', 'side-by-side')],
      current: [],
    }
  }

  if (!previous || !current) {
    return { previous: [], current: [] }
  }

  if (previous.type === 'text' && current.type === 'text') {
    return annotateTextPair(previous, current)
  }

  if (previous.type !== current.type) {
    return {
      previous: [markTextInNode(previous, 'deleted', 'side-by-side')],
      current: [markTextInNode(current, 'added', 'side-by-side')],
    }
  }

  const previousContent = previous.content ?? []
  const currentContent = current.content ?? []
  const annotatedPreviousContent: JSONContent[] = []
  const annotatedCurrentContent: JSONContent[] = []

  for (const pair of alignChildNodes(previousContent, currentContent)) {
    const annotated = annotateNodePair(pair.previous, pair.current)
    annotatedPreviousContent.push(...annotated.previous)
    annotatedCurrentContent.push(...annotated.current)
  }

  return {
    previous: [
      {
        ...previous,
        ...(annotatedPreviousContent.length > 0
          ? { content: annotatedPreviousContent }
          : {}),
      },
    ],
    current: [
      {
        ...current,
        ...(annotatedCurrentContent.length > 0
          ? { content: annotatedCurrentContent }
          : {}),
      },
    ],
  }
}

function annotateInlineNodePair(
  previous?: JSONContent,
  current?: JSONContent
): JSONContent[] {
  if (!previous && !current) {
    return []
  }

  if (!previous && current) {
    return [markTextInNode(current, 'added', 'inline')]
  }

  if (previous && !current) {
    return [markTextInNode(previous, 'deleted', 'inline')]
  }

  if (!previous || !current) {
    return []
  }

  if (previous.type === 'text' && current.type === 'text') {
    return annotateInlineTextPair(previous, current)
  }

  if (previous.type !== current.type) {
    return [
      markTextInNode(previous, 'deleted', 'inline'),
      markTextInNode(current, 'added', 'inline'),
    ]
  }

  const previousContent = previous.content ?? []
  const currentContent = current.content ?? []
  const content = alignChildNodes(previousContent, currentContent).flatMap(
    (pair) => annotateInlineNodePair(pair.previous, pair.current)
  )

  return [
    {
      ...current,
      ...(content.length > 0 ? { content } : {}),
    },
  ]
}

function alignChildNodes(previous: JSONContent[], current: JSONContent[]) {
  const deleteCost = 1
  const insertCost = 1
  const previousLength = previous.length
  const currentLength = current.length
  const costs = Array.from({ length: previousLength + 1 }, () =>
    Array<number>(currentLength + 1).fill(0)
  )
  const choices = Array.from({ length: previousLength + 1 }, () =>
    Array<'pair' | 'delete' | 'insert' | null>(currentLength + 1).fill(null)
  )

  for (let i = previousLength - 1; i >= 0; i--) {
    costs[i][currentLength] = deleteCost + costs[i + 1][currentLength]
    choices[i][currentLength] = 'delete'
  }

  for (let j = currentLength - 1; j >= 0; j--) {
    costs[previousLength][j] = insertCost + costs[previousLength][j + 1]
    choices[previousLength][j] = 'insert'
  }

  for (let i = previousLength - 1; i >= 0; i--) {
    for (let j = currentLength - 1; j >= 0; j--) {
      const pairCost = getPairCost(previous[i], current[j])
      const pairTotal = pairCost + costs[i + 1][j + 1]
      const deleteTotal = deleteCost + costs[i + 1][j]
      const insertTotal = insertCost + costs[i][j + 1]

      if (pairTotal <= deleteTotal && pairTotal <= insertTotal) {
        costs[i][j] = pairTotal
        choices[i][j] = 'pair'
      } else if (deleteTotal <= insertTotal) {
        costs[i][j] = deleteTotal
        choices[i][j] = 'delete'
      } else {
        costs[i][j] = insertTotal
        choices[i][j] = 'insert'
      }
    }
  }

  const pairs: { previous?: JSONContent; current?: JSONContent }[] = []
  let i = 0
  let j = 0

  while (i < previousLength || j < currentLength) {
    const choice = choices[i][j]

    if (choice === 'pair') {
      pairs.push({ previous: previous[i], current: current[j] })
      i++
      j++
    } else if (choice === 'delete') {
      pairs.push({ previous: previous[i] })
      i++
    } else {
      pairs.push({ current: current[j] })
      j++
    }
  }

  return pairs
}

function getPairCost(previous: JSONContent, current: JSONContent) {
  if (previous.type !== current.type) {
    return Number.POSITIVE_INFINITY
  }

  if (getStableNodeKey(previous) === getStableNodeKey(current)) {
    return 0
  }

  const previousText = getNodeText(previous)
  const currentText = getNodeText(current)
  if (!previousText && !currentText) {
    return 0.8
  }

  const similarity = getTextSimilarity(previousText, currentText)
  if (similarity >= 0.6) {
    return 0.25
  }

  if (similarity >= 0.35) {
    return 0.7
  }

  return 1.4
}

function getStableNodeKey(node: JSONContent) {
  if (node.attrs?.id) {
    return `${node.type}:${node.attrs.id}`
  }

  return `${node.type}:${getNodeText(node)}`
}

function getNodeText(node: JSONContent): string {
  if (node.type === 'text') {
    return node.text ?? ''
  }

  return (node.content ?? []).map(getNodeText).join('\n').trim()
}

function getTextSimilarity(previous: string, current: string) {
  if (previous === current) {
    return 1
  }

  if (!previous || !current) {
    return 0
  }

  const previousTokens = new Set(previous.toLowerCase().split(/\s+/))
  const currentTokens = new Set(current.toLowerCase().split(/\s+/))
  const intersection = [...previousTokens].filter((token) =>
    currentTokens.has(token)
  ).length
  const union = new Set([...previousTokens, ...currentTokens]).size

  return union === 0 ? 0 : intersection / union
}

function annotateTextPair(previous: JSONContent, current: JSONContent) {
  const previousText = previous.text ?? ''
  const currentText = current.text ?? ''

  if (previousText === currentText) {
    return { previous: [previous], current: [current] }
  }

  let prefixLength = 0
  while (
    prefixLength < previousText.length &&
    prefixLength < currentText.length &&
    previousText[prefixLength] === currentText[prefixLength]
  ) {
    prefixLength++
  }

  let suffixLength = 0
  while (
    suffixLength < previousText.length - prefixLength &&
    suffixLength < currentText.length - prefixLength &&
    previousText[previousText.length - 1 - suffixLength] ===
      currentText[currentText.length - 1 - suffixLength]
  ) {
    suffixLength++
  }

  return {
    previous: splitTextNode(
      previous,
      prefixLength,
      suffixLength,
      'deleted',
      'side-by-side'
    ),
    current: splitTextNode(
      current,
      prefixLength,
      suffixLength,
      'added',
      'side-by-side'
    ),
  }
}

function annotateInlineTextPair(previous: JSONContent, current: JSONContent) {
  const previousText = previous.text ?? ''
  const currentText = current.text ?? ''

  if (previousText === currentText) {
    return [current]
  }

  let prefixLength = 0
  while (
    prefixLength < previousText.length &&
    prefixLength < currentText.length &&
    previousText[prefixLength] === currentText[prefixLength]
  ) {
    prefixLength++
  }

  let suffixLength = 0
  while (
    suffixLength < previousText.length - prefixLength &&
    suffixLength < currentText.length - prefixLength &&
    previousText[previousText.length - 1 - suffixLength] ===
      currentText[currentText.length - 1 - suffixLength]
  ) {
    suffixLength++
  }

  const previousChangedEnd = previousText.length - suffixLength
  const currentChangedEnd = currentText.length - suffixLength
  const chunks: JSONContent[] = []

  const pushText = (
    source: JSONContent,
    value: string,
    tone?: DiffTone
  ) => {
    if (!value) return
    chunks.push({
      ...source,
      text: value,
      marks: tone ? addDiffMark(source.marks, tone, 'inline') : source.marks,
    })
  }

  pushText(current, currentText.slice(0, prefixLength))
  pushText(
    previous,
    previousText.slice(prefixLength, previousChangedEnd),
    'deleted'
  )
  pushText(
    current,
    currentText.slice(prefixLength, currentChangedEnd),
    'added'
  )
  pushText(current, currentText.slice(currentChangedEnd))

  return chunks
}

function splitTextNode(
  node: JSONContent,
  prefixLength: number,
  suffixLength: number,
  tone: DiffTone,
  presentation: DiffPresentation
) {
  const text = node.text ?? ''
  const changedEnd = text.length - suffixLength
  const chunks: JSONContent[] = []

  const pushText = (value: string, changed: boolean) => {
    if (!value) return
    chunks.push({
      ...node,
      text: value,
      marks: changed ? addDiffMark(node.marks, tone, presentation) : node.marks,
    })
  }

  pushText(text.slice(0, prefixLength), false)
  pushText(text.slice(prefixLength, changedEnd), true)
  pushText(text.slice(changedEnd), false)

  return chunks
}

function markTextInNode(
  node: JSONContent,
  tone: DiffTone,
  presentation: DiffPresentation
): JSONContent {
  if (node.type === 'text') {
    return {
      ...node,
      marks: addDiffMark(node.marks, tone, presentation),
    }
  }

  if (!node.content) {
    return { ...node }
  }

  return {
    ...node,
    content: node.content.map((child) =>
      markTextInNode(child, tone, presentation)
    ),
  }
}

function addDiffMark(
  marks: JSONContent['marks'],
  tone: DiffTone,
  _presentation: DiffPresentation
) {
  const diffMark = tone === 'added' ? ADDED_MARK : DELETED_MARK
  const nextMarks = [...(marks ?? []), diffMark]

  if (tone === 'deleted') {
    nextMarks.push(STRIKE_MARK)
  }

  return nextMarks
}
